import React, { useState, useEffect } from 'react';
import { 
  History, ShieldCheck, ExternalLink, Calendar, 
  Search, RefreshCw, AlertCircle, Eye, CheckCircle2, Lock
} from 'lucide-react';
import { managerAPI } from '../utils/api';

const ManagerAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res = await managerAPI.getAuditLogs();
      if (res.data?.success) {
        setLogs(res.data.audit_logs || []);
      }
    } catch (err) {
      console.error("Failed to fetch manager audit logs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = logs.filter(l => {
    const term = search.toLowerCase();
    const action = (l.action || '').toLowerCase();
    const entity = (l.entity_type || '').toLowerCase();
    const entityId = (l.entity_id || '').toLowerCase();
    return action.includes(term) || entity.includes(term) || entityId.includes(term);
  });

  const getActionColor = (action) => {
    if (action.includes('BAN')) return 'bg-rose-500/10 text-rose-400 border-rose-500/20';
    if (action.includes('APPROVAL')) return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    if (action.includes('ASSIGN')) return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
    if (action.includes('CLOSE')) return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
    return 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20';
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-white/5">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-extrabold text-white tracking-tight">Executive Audit Records</h1>
            <span className="px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 font-bold text-xs uppercase flex items-center gap-1.5">
              <Lock size={12} /> Immutable Trail
            </span>
          </div>
          <p className="text-slate-400 text-sm mt-1">
            Complete chronological record of all administrative activities performed under your Manager credentials.
          </p>
        </div>

        <button 
          onClick={fetchLogs}
          className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-slate-300 font-semibold text-sm transition-colors cursor-pointer w-max"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          <span>Refresh Records</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input
          type="text"
          placeholder="Search by action, target entity..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-[#0b0f19] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition-colors"
        />
      </div>

      {/* Logs Table */}
      <div className="bg-[#0b0f19] border border-white/5 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-300">
            <thead className="bg-white/[0.02] text-xs uppercase text-slate-400 tracking-wider border-b border-white/5">
              <tr>
                <th className="py-4 px-6">Timestamp</th>
                <th className="py-4 px-6">Action</th>
                <th className="py-4 px-6">Target Entity</th>
                <th className="py-4 px-6">Details / Summary</th>
                <th className="py-4 px-6">Mandatory Proof</th>
                <th className="py-4 px-6">IP Address</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-slate-400">
                    <RefreshCw size={20} className="animate-spin text-indigo-400 mx-auto mb-2" />
                    Loading audit trail...
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-slate-400">
                    No logged actions recorded yet.
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-white/[0.01] transition-colors">
                    <td className="py-4 px-6 font-mono text-xs text-slate-400 whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="font-bold text-white text-xs">{log.entity_type}</div>
                      <div className="font-mono text-[11px] text-indigo-400">{log.entity_id}</div>
                    </td>
                    <td className="py-4 px-6 text-xs text-slate-300 max-w-xs">
                      {typeof log.details === 'object' && log.details !== null
                        ? Object.entries(log.details).map(([k, v]) => (
                            <span key={k} className="inline-block mr-2 text-[11px]">
                              <strong>{k}:</strong> {String(v)}
                            </span>
                          ))
                        : String(log.details || '-')}
                    </td>
                    <td className="py-4 px-6">
                      {log.proof_url ? (
                        <a 
                          href={log.proof_url} 
                          target="_blank" 
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 text-xs font-semibold transition-colors"
                        >
                          <ExternalLink size={12} />
                          <span>View Proof</span>
                        </a>
                      ) : (
                        <span className="text-xs text-slate-600">N/A</span>
                      )}
                    </td>
                    <td className="py-4 px-6 font-mono text-[11px] text-slate-500">
                      {log.ip_address || '127.0.0.1'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManagerAuditLogs;
